import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import FileUploadMain from "@/components/ui/FileUploadMain";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { appConfig } from "@/lib/AppConfig";
import { formatArea, formatLength } from "@/lib/geospacial-utils";
import { ValidationError } from "@/types/problemDetails";
import { AnnulusPointOfInterest, Feature, GeometeryType, PolygonPointOfInterest, PolylinePointOfInterest, } from "@/lib/models";
import { setFormErrors } from "@/lib/react-form-utils";
import { featurePriorityColour } from "@/lib/task-utils";
import { areaIndexUrlBuilder, featureTypeUrlBuilder, siteMapUrlBuilder } from "@/lib/url-builder";
import { Area, AreaService } from "@/services/AreaService";
import { CacheService } from "@/services/CacheService";
import { Code, CodeService } from "@/services/CodeService";
import { FeatureService } from "@/services/FeatureService";
import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useParams } from "react-router";
import { Map } from "lucide-react";
import ImageList from "@/components/ImageList";
import { AttachmentService, AttachmentType, type AttachmentDetails } from "@/services/AttachmenteService";
import { FileUploadCopied } from "@/components/ui/FilesUploadCopied";

type FeatureWithoutMetadata = Omit<
  Feature,
  | "id"
  | "hpi"
  | "plannedConditionCodeId"
  | "plannedDate"
  | "enhancementOpportunities"
  | "nativePlantSpecies"
  | "invasivePlantSpecies"
  | "animalsObserved"
  | "coordinates"
  | "heading"
  | "zoom"
  | "centre"
  | "type"
  | "attachments"
  | "siteId"
  | "tenantId"
>;

const FeatureEditPage: React.FC = () => {
  const params = useParams();
  const {register, handleSubmit, formState: { isDirty, errors }, reset, control, setError } = useForm<FeatureWithoutMetadata>();
  const [areaId, priorityCodeId] = useWatch({ control, name: ["areaId", "priorityCodeId"] })
  const isSaveDisabled = !isDirty;
  const [areas, setAreas] = useState<Area[]>([]);
  const [featureName, setFeatureName] = useState<string>("");
  const [codes, setCodes] = useState<Code[]>([]);
  const [conditionCodes, setConditionCodes] = useState<Code[]>([])
  const [featureTypeCode, setFeatureTypeCode] = useState<Code | null>(null)
  const [measurements, setMeasurements] = useState<string[]>([])
  const [attachmentDetails, setAttachmentDetails] = useState<AttachmentDetails[]>([]);
  const [siteId, setSiteId] = useState<string>("")
  const [tenantId, setTenantId] = useState<string>("")

  useEffect(() => {
    const fetchFeature = async () => {
      const [codeList, areaList, feature, attachmentList] = await Promise.all([
        CacheService.get("allcodes", appConfig.resourceTypes.code, async () => {
          return CodeService.getCodes2();
        }),
        AreaService.getBySiteId(params.siteId!),
        FeatureService.getById(params.featureId!),
        AttachmentService.getByEntityId(params.featureId!),
      ]);      
      if (!feature) {
        console.error(`Feature with ID ${params.featureId} not found`);
        return;
      }
      setCodes(codeList);
      setConditionCodes(codeList.filter(c => c.type === "Condition"))
      setAreas(areaList);
      setAttachmentDetails(attachmentList);
      setSiteId(feature.siteId);
      setTenantId(feature.tenantId);

      setFeatureTypeCode(codeList.find(c => c.id === feature.codeId) ?? null)
      setFeatureName(feature.name)
      reset(feature);
      if (feature.type === GeometeryType.Polygon) {
        const poly = feature as PolygonPointOfInterest
        setMeasurements([`Area: ${formatArea(poly.area)}`, `Perimeter: ${formatLength(poly.perimeter)}`]);
      } else if (feature.type === GeometeryType.Annulus) {
        const annulus = feature as AnnulusPointOfInterest
        setMeasurements([`Area: ${formatArea(annulus.area)}`, `Perimeter: ${formatLength(annulus.perimeter)}`]);
      } else if (feature.type === GeometeryType.Line) { //
        setMeasurements([`Length: ${formatLength((feature as PolylinePointOfInterest).length)}`]);
      }
    };
    fetchFeature();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.featureId, params.siteId]);

  const resetAction = (action: FeatureWithoutMetadata) => {
    reset(action)
  }

  const onSubmit = async (formData: FeatureWithoutMetadata) => {
    const updatedFeature: Feature = {
      id: params.featureId!,
      animalsObserved: null,
      attachments: [],
      centre: { latitude: 0, longitude: 0 },
      enhancementOpportunities: null,
      heading: 0,
      coordinates: [],
      hpi: false,
      invasivePlantSpecies: null,
      nativePlantSpecies: null,
      plannedConditionCodeId: null,
      plannedDate: null,
      type: GeometeryType.Point,
      zoom: 0,
      tenantId: tenantId,
      siteId: siteId,
      ...formData,
    };

    updatedFeature.areaId = formData.areaId === "" ? null : formData.areaId;

    try {
        await FeatureService.updateFeature(params.featureId!, updatedFeature);
        setFeatureName(formData.name)
        resetAction(updatedFeature)
        CacheService.clear(appConfig.resourceTypes.feature, updatedFeature.id!);
    } catch (error) {
        if (error instanceof ValidationError) {
            setFormErrors<FeatureWithoutMetadata>(error.errors, setError, formData)
        }
    }
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center gap-2">
        <Link to={siteMapUrlBuilder(params.tenantId!, params.siteId!, params.featureId!)}>
          <Map className="group-hover:text-blue-600 transition-colors duration-200 cursor-pointer" />
        </Link>
        <Header headerTitle={`Feature: ${featureName}`} />
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="pt-4"> Feature type:
          {featureTypeCode ? (
            <Link  
              to={featureTypeUrlBuilder(params.tenantId!, params.siteId!, featureTypeCode.id!)}
              className="text-blue-600 cursor-pointer">{featureTypeCode.name}</Link>
          ) : (
            <></>
          )}
        </div>
        <div>
          <Label
            htmlFor="name"
            className="block text-sm font-medium text-gray-700"
          >
            Name *
          </Label>
          <Input
            id="name"
            type="text"
            {...register("name", { required: 'Name is required'})}
          />
          <span className="text-sm text-red-500">{errors.name?.message}</span>
        </div>
        <div>
          <Label
            htmlFor="areaId"
            className="block text-sm font-medium text-gray-700"
          >
            {areaId ? <Link className="text-blue-500" to={areaIndexUrlBuilder(params.tenantId!, params.siteId!, areaId!)}>Area</Link> : <>Area</>}
          </Label>


          <select
            id="areaId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("areaId", { setValueAs: (value) => (value === "" ? null : value),})}
          >
            <option value="">
              Select an area
            </option>
              {areas.length > 0 && (
                <>
                  {areas
                    .map((area) => (
                      <option key={area.id} value={area.id}>
                        {area.name}
                      </option>
                    ))}
                </>
              )}
          </select> 
        </div>
        <div className="flex flex-col">
          <FileUploadMain entityId={params.featureId!} tenantId={tenantId} siteId={params.siteId!} entityType="Feature" />
        </div>
        <div>
          <Label
            htmlFor="ref"
            className="block text-sm font-medium text-gray-700"
          >
            Reference
          </Label>
          <Input
            id="ref"
            type="text"
            {...register("ref", { maxLength: { value: 10, message: 'Reference is too long' }})}
          />
          <span className="text-sm text-red-500">{errors.ref?.message}</span>
        </div>
        <div>{measurements.join(' | ')}</div>
        <div>
          <label
            htmlFor="description"
            className="block text-sm font-medium text-gray-700"
          >
            Description
          </label>
          <Textarea
            id="description"
            {...register("description", { maxLength: { value: 2000, message: 'Description is too long' }})}
            placeholder="Enter feature description..."
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                //savePolygon()
              }
            }}
          /> 
          <span className="text-sm text-red-500">{errors.name?.message}</span>
        </div>
        <div>
          <label
            htmlFor="priorityCodeId"
            className="block text-sm font-medium text-gray-700"
          >
            Priority
          </label>
          <select
            id="priorityCodeId"
            className={`flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1 ${featurePriorityColour[priorityCodeId]}`}
            {...register("priorityCodeId", { required: 'Priority is required'})}
          >
              {codes.length > 0 && (
                <>
                  {codes
                    .filter((c) => c.type === "Priority")
                    .map((priorityCode) => (
                      <option key={priorityCode.id} value={priorityCode.id} className={featurePriorityColour[priorityCode.id]}>
                        {priorityCode.name}
                      </option>
                    ))}
                </>
              )}
          </select>
        </div>
        <div>
          <Label htmlFor="currentConditionCodeId">Current Condition</Label>
          <select
            id="currentConditionCodeId"
            className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1"
            {...register("currentConditionCodeId", { required: 'Priority is required'})}
          >
              {codes.length > 0 && (
                <>
                  {conditionCodes
                    .map((conditionCode) => (
                      <option key={conditionCode.id} value={conditionCode.id}>
                        {conditionCode.name}
                      </option>
                    ))}
                </>
              )}
          </select>
        </div>
        <div>
          <label
            htmlFor="conditionSummary"
            className="block text-sm font-medium text-gray-700"
          >
            Condition Summary
          </label>
          <Textarea
            id="conditionSummary"
            {...register("conditionSummary", { maxLength: { value: 4000, message: 'Condition summary is too long' }})}
            placeholder="Enter condition summary..."
          /> 
          <span className="text-sm text-red-500">{errors.conditionSummary?.message}</span>
        </div>
        <div className="flex justify-end space-x-2">
          <button
            type="button"
            onClick={() => reset()}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <Button
            type="submit"
            disabled={isSaveDisabled}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Update Feature
          </Button>
        </div>
      </form>       
      <div className="my-2">
        <FileUploadCopied
          attachmentType={AttachmentType.None}
          entityId={params.featureId!}
          siteId={params.siteId!}
          tenantId={params.tenantId!}
          entityType="Feature"
          onFileUploaded={(attachment) => {
            setAttachmentDetails((prev) => [
              {
                ...attachment,
              },
              ...prev,
            ]);
          }}
          buttonText="Select Images"
        />
      </div>     
      <div className="grow">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <ImageList
            attachmentColumn={false}
            attachments={attachmentDetails}
            onAttachmentsChange={setAttachmentDetails}
            onDeleteAttachment={(attachmentId: string) => {
              try {
                AttachmentService.delete(attachmentId);
                setAttachmentDetails(prev => prev.filter(att => att.id !== attachmentId));
              } catch (error) {
                alert("Error deleting attachment");
                throw error;
              }
            }}
          />          
        </div>
      </div>
    </div>
  );
};

export default FeatureEditPage;
